/**
 * Collapse product×warehouse stock-position rows into one row per product,
 * summing quantity and value fields across warehouses.
 */

/** Values stored in filter state / SelectItem `value`. */
export const STOCK_POSITION_OTHER_FILTER = {
  ALL: "All",
  DEAD_STOCK: "Dead Stock",
  DEFECTIVE: "Defective Stock",
  RESERVED: "Reserved Stock",
  SERVICE_SAFETY: "Service Safety Stock",
  SALES_SAFETY: "Sales Safety Stock",
};

/** @deprecated label renamed; map saved filters from older sessions. */
const LEGACY_MAINTENANCE_RESERVE_FILTER = "Maintenance Reserve Stock";

export function normalizeStockPositionOtherFilter(otherFilter) {
  if (!otherFilter || otherFilter === STOCK_POSITION_OTHER_FILTER.ALL) {
    return STOCK_POSITION_OTHER_FILTER.ALL;
  }
  if (otherFilter === LEGACY_MAINTENANCE_RESERVE_FILTER) {
    return STOCK_POSITION_OTHER_FILTER.SERVICE_SAFETY;
  }
  return otherFilter;
}

const SUM_FIELDS = [
  "totalStock",
  "reservedStock",
  "reservedStockValue",
  "maintenanceReserve",
  "maintenanceReserveValue",
  "defectiveStock",
  "defectiveValue",
  "availableStock",
  "stockValue",
  "availableStockValue",
  "pendingRevokes",
  "reorderQuantity",
];

function latestDate(a, b) {
  if (!a) return b || null;
  if (!b) return a;
  return String(a) >= String(b) ? a : b;
}

/**
 * @param {Array<object>} rows
 * @returns {Array<object>}
 */
export function aggregateStockPositionsByProduct(rows = []) {
  const byProduct = new Map();

  rows.forEach((row) => {
    if (!row) return;
    const key = row.productId != null && row.productId !== ""
      ? String(row.productId)
      : `name:${row.productName || ""}`;

    const existing = byProduct.get(key);
    if (!existing) {
      byProduct.set(key, {
        ...row,
        id: String(row.productId || key),
        warehouseLocationId: "",
        warehouse: "",
        warehouses: row.warehouseLocationId
          ? [{ id: row.warehouseLocationId, name: row.warehouse }]
          : [],
      });
      return;
    }

    SUM_FIELDS.forEach((field) => {
      existing[field] = (Number(existing[field]) || 0) + (Number(row[field]) || 0);
    });

    // Safety stocks are warehouse×product config. Sum across warehouses so the
    // product row reflects total held safety; prefer mapped serviceSafetyStock.
    existing.serviceSafetyStock =
      (Number(existing.serviceSafetyStock) || 0) + (Number(row.serviceSafetyStock) || 0);
    existing.salesSafetyStock =
      (Number(existing.salesSafetyStock) || 0) + (Number(row.salesSafetyStock) || 0);
    existing.serviceSafetyStockValue =
      (Number(existing.serviceSafetyStockValue) || 0) +
      (Number(row.serviceSafetyStockValue) || 0);
    existing.salesSafetyStockValue =
      (Number(existing.salesSafetyStockValue) || 0) +
      (Number(row.salesSafetyStockValue) || 0);
    existing.serviceAvailable =
      (Number(existing.serviceAvailable) || 0) + (Number(row.serviceAvailable) || 0);
    existing.salesAvailable =
      (Number(existing.salesAvailable) || 0) + (Number(row.salesAvailable) || 0);
    existing.serviceAvailableValue =
      (Number(existing.serviceAvailableValue) || 0) +
      (Number(row.serviceAvailableValue) || 0);
    existing.salesAvailableValue =
      (Number(existing.salesAvailableValue) || 0) +
      (Number(row.salesAvailableValue) || 0);

    existing.isDeadStock = Boolean(existing.isDeadStock) || Boolean(row.isDeadStock);
    existing.lastOutwardMovementDate = latestDate(
      existing.lastOutwardMovementDate,
      row.lastOutwardMovementDate
    );

    if (row.warehouseLocationId) {
      existing.warehouses.push({
        id: row.warehouseLocationId,
        name: row.warehouse,
      });
    }

    // Prefer non-empty product metadata from either row
    if (!existing.partNumber && row.partNumber) existing.partNumber = row.partNumber;
    if (!existing.systemName && row.systemName) existing.systemName = row.systemName;
    if (!existing.category && row.category) existing.category = row.category;
    if (!existing.productName && row.productName) existing.productName = row.productName;
    if (!(Number(existing.listPrice) > 0) && Number(row.listPrice) > 0) {
      existing.listPrice = row.listPrice;
    }
  });

  return Array.from(byProduct.values());
}

/**
 * Apply "Other Filters" to product-level (or warehouse-level) rows.
 * @param {Array<object>} rows
 * @param {string} otherFilter
 */
export function applyStockPositionOtherFilter(rows = [], otherFilter = "All") {
  const filter = normalizeStockPositionOtherFilter(otherFilter);
  if (filter === STOCK_POSITION_OTHER_FILTER.ALL) return rows;
  // Dead stock is filtered server-side via deadStock=DEAD — do not filter client-side
  if (filter === STOCK_POSITION_OTHER_FILTER.DEAD_STOCK) {
    return rows;
  }
  if (filter === STOCK_POSITION_OTHER_FILTER.DEFECTIVE) {
    return rows.filter((row) => (Number(row.defectiveStock) || 0) > 0);
  }
  if (filter === STOCK_POSITION_OTHER_FILTER.SERVICE_SAFETY) {
    return rows.filter(
      (row) =>
        (Number(row.serviceSafetyStock ?? row.maintenanceReserve) || 0) > 0
    );
  }
  if (filter === STOCK_POSITION_OTHER_FILTER.SALES_SAFETY) {
    return rows.filter((row) => (Number(row.salesSafetyStock) || 0) > 0);
  }
  if (filter === STOCK_POSITION_OTHER_FILTER.RESERVED) {
    return rows.filter((row) => (Number(row.reservedStock) || 0) > 0);
  }
  return rows;
}

/**
 * Rebuild system value summaries from product-level rows.
 * @param {Array<object>} rows
 * @param {string} otherFilter
 */
export function buildSystemSummariesFromRows(rows = [], otherFilter = "All") {
  const filter = normalizeStockPositionOtherFilter(otherFilter);
  const bySystem = new Map();
  rows.forEach((row) => {
    const key = row.systemName || "—";
    let value = Number(row.availableStockValue) || 0;
    if (filter === STOCK_POSITION_OTHER_FILTER.DEFECTIVE) {
      value = Number(row.defectiveValue) || 0;
    } else if (filter === STOCK_POSITION_OTHER_FILTER.SERVICE_SAFETY) {
      value =
        Number(row.serviceSafetyStockValue ?? row.maintenanceReserveValue) || 0;
    } else if (filter === STOCK_POSITION_OTHER_FILTER.SALES_SAFETY) {
      value = Number(row.salesSafetyStockValue) || 0;
    } else if (filter === STOCK_POSITION_OTHER_FILTER.RESERVED) {
      value = Number(row.reservedStockValue) || 0;
    }
    bySystem.set(key, (bySystem.get(key) || 0) + value);
  });
  return Array.from(bySystem.entries()).map(([systemName, total]) => ({
    systemName,
    total,
  }));
}
